import { DEFAULT_STORY } from "@/shared/constants";
import { resolveDesignSystem } from "@/pages/editor/modules/design-system/modules/theme/utils/resolve-design-system";
import { useGetThemesQuery } from "@/shared/services";
import { withDesignSystemDefaults } from "@/shared/utils/design-system-defaults";
import {
  handleDesign,
  loadGoogleFontsForDesign,
} from "@/shared/utils/handle-design";
import { resolveDesignSystemVariables } from "@/shared/utils/resolve-design-system-variables";
import { type CSSProperties, useCallback, useEffect, useMemo } from "react";
import { useImportStore } from "../stores/import-store-context";

// The design system the created story will get, resolved the same way for the
// previews so the options look like the final slides.
export const useImportDesignSystem = () => {
  const { selectedDesignSystemId, designColors } = useImportStore((s) => ({
    selectedDesignSystemId: s.selectedDesignSystemId,
    designColors: s.designColors,
  }));
  const { data: themes } = useGetThemesQuery({ enabled: true });

  const designSystem = useMemo(() => {
    const resolved = resolveDesignSystem({
      base: structuredClone(DEFAULT_STORY.settings.designSystemV2),
      selectedDesignSystemId,
      themes,
      pdfDesignColors: designColors,
    });
    return withDesignSystemDefaults(
      resolved.designSystemV2 as Record<string, unknown>,
    ) as unknown as Record<string, unknown>;
  }, [selectedDesignSystemId, designColors, themes]);

  const style = useMemo(() => {
    const vars: Record<string, string> = {};
    for (const [name, value] of resolveDesignSystemVariables(designSystem))
      vars[name] = value;
    return vars as CSSProperties;
  }, [designSystem]);

  useEffect(() => {
    loadGoogleFontsForDesign(designSystem);
  }, [designSystem]);

  // Paints the resolved theme onto a container the way the funnel's previews
  // do (shared handleDesign: variables + boolean transforms).
  const designRef = useCallback(
    (node: HTMLElement | null) =>
      handleDesign(designSystem as Record<string, string>, node),
    [designSystem],
  );

  return { designSystem, style, designRef, themes };
};
